/**
 * @fileoverview 市场预测卡片组件
 * @description 展示单个预测市场的核心信息，包括标题、Yes/No 概率、交易量、截止时间
 * 设计风格：深色卡片，黄色悬停边框，绿/红双色进度条，资产符号标签
 * @module components/market/MarketCard
 */

import { Clock, Users, TrendingUp, TrendingDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { formatLargeNumber, getAssetIconUrl } from '../../lib/utils';
import { fetchClosePrice } from '../../services/marketService';
import { API_OK } from '../../services/api';
import type { Market } from '../../types';

/**
 * 截止时间倒计时组件
 */
function EndTimeCountdown({ endTime }: { endTime: string }) {
  const { t } = useTranslation();
  const calcLabel = () => {
    const diff = Math.floor((new Date(endTime).getTime() - Date.now()) / 1000);
    if (diff <= 0) return '__expired__';
    const days = Math.floor(diff / 86400);
    if (days > 0) return `__days__${days}`;
    const hours = Math.floor(diff / 3600);
    if (hours > 0) return `__hours__${hours}`;
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const [label, setLabel] = useState(calcLabel);

  useEffect(() => {
    const timer = setInterval(() => setLabel(calcLabel()), 1000);
    return () => clearInterval(timer);
  }, [endTime]);

  const isExpired = label === '__expired__';
  const displayLabel = label === '__expired__'
    ? t('market.card.expired')
    : label.startsWith('__days__')
    ? t('market.card.daysLater', { n: label.replace('__days__', '') })
    : label.startsWith('__hours__')
    ? t('market.card.hoursLater', { n: label.replace('__hours__', '') })
    : label;

  return (
    <span className={isExpired ? 'text-muted-foreground/60' : 'text-brand font-mono font-semibold text-xs'}>
      {displayLabel}
    </span>
  );
}

interface MarketCardProps {
  market: Market;
  animationDelay?: number;
  /** 用户是否正在参与此市场（有未结算订单） */
  isParticipating?: boolean;
}

export default function MarketCard({ market, animationDelay = 0, isParticipating = false }: MarketCardProps) {
  const { t } = useTranslation();

  // 事件类市场也展示「目标价 + 当前价涨跌」（原来这块是隐形占位，视觉上是一片空白）
  const targetPrice = market.targetPrice ?? 0;
  const hasTargetPrice = targetPrice > 0;
  const [livePrice, setLivePrice] = useState<number | null>(null);

  useEffect(() => {
    if (!hasTargetPrice || !market.assetSymbol) return;
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        const resp = await fetchClosePrice(market.id);
        if (!cancelled && resp.status === API_OK && resp.data) {
          const p = resp.data.price;
          if (p !== null && p !== undefined && Number.isFinite(Number(p))) setLivePrice(Number(p));
        }
      } catch { /* 静默失败 */ }
    };
    fetchPrice();
    const timer = setInterval(fetchPrice, 30_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [market.id, market.assetSymbol, hasTargetPrice]);

  const priceDiff = livePrice !== null ? livePrice - targetPrice : 0;
  const isUp = priceDiff >= 0;
  const diffPercent = (hasTargetPrice && livePrice !== null)
    ? ((priceDiff / targetPrice) * 100).toFixed(2)
    : null;

  const statusStyles: Record<string, { bg: string; text: string }> = {
    active:    { bg: 'bg-[#0ECB81]/10', text: 'text-[#0ECB81]' },
    resolved:  { bg: 'bg-white/6',      text: 'text-muted-foreground' },
    cancelled: { bg: 'bg-[#F6465D]/10', text: 'text-[#F6465D]' },
    pending:   { bg: 'bg-brand/10',     text: 'text-brand' },
    closed:    { bg: 'bg-white/5',      text: 'text-muted-foreground/60' },
  };
  const st = statusStyles[market.status] ?? statusStyles.active;

  return (
    <Link href={`/markets/${market.id}`}>
      <div
        className="group relative flex flex-col h-full bg-[#1A1D24] rounded-xl p-4 cursor-pointer animate-fade-in-up transition-all duration-200 border border-white/5 hover:border-brand/30 hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        {/* 悬停光晕 */}
        <div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at top left, rgba(240,185,11,0.05) 0%, transparent 65%)' }}
        />
        {/* 头部：图标 + 资产 + 标签 */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
              <img src={getAssetIconUrl(market.assetSymbol, market.category)} alt={market.assetSymbol || market.category} className="w-5 h-5 object-contain" />
            </div>
            <div className="leading-none">
              {market.assetSymbol && (
                <span className="text-[11px] text-brand font-mono font-bold block mb-0.5">
                  {market.assetSymbol}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground/70 capitalize">
                {t(`market.category.${market.category}`, { defaultValue: market.category.replace(/_/g, ' ') })}
              </span>
            </div>
          </div>
          {/* 标签组：参与中 + Hot/New + 状态 */}
          <div className="flex items-center gap-1">
            {isParticipating && (
              <span
                className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                style={{ background: 'rgba(14,203,129,0.15)', border: '1px solid rgba(14,203,129,0.35)', color: '#0ECB81' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#0ECB81] animate-pulse" />
                {t('marketBadge.participating')}
              </span>
            )}
            {market.isNew && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 font-semibold">
                NEW
              </span>
            )}
            {market.isHot && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20 font-semibold">
                {t('market.card.hot')}
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${st.bg} ${st.text}`}>
              {t(`market.status.${market.status}`)}
            </span>
          </div>
        </div>

        {/* 市场标题 */}
        <h3
          className="text-sm font-semibold text-foreground/90 leading-snug mb-3 line-clamp-2 min-h-[2.5rem] group-hover:text-white transition-colors"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          {market.title}
        </h3>

        {/* 价格区域：有目标价则展示「目标价 + 当前涨跌」；否则保持隐形占位与高频卡片等高 */}
        <div
          className={`flex items-center justify-between mb-3 py-2 px-3 rounded-lg bg-white/3 border border-white/5${
            hasTargetPrice ? '' : ' invisible'
          }`}
        >
          {hasTargetPrice ? (
            <>
              <div>
                <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.detail.targetPriceLabel')}</p>
                <p className="text-xs font-mono font-bold text-white">
                  ${targetPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.detail.currentShort')}</p>
                {livePrice === null ? (
                  <span className="text-[10px] text-muted-foreground animate-pulse">{t('market.detail.fetching')}</span>
                ) : (
                  <div className="flex items-center gap-1 justify-end">
                    {isUp
                      ? <TrendingUp className="h-3 w-3 text-[#0ECB81]" />
                      : <TrendingDown className="h-3 w-3 text-[#F6465D]" />
                    }
                    <span className="text-xs font-mono font-bold" style={{ color: isUp ? '#0ECB81' : '#F6465D' }}>
                      {isUp ? '+' : ''}{diffPercent}%
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-[10px] mb-0.5">&nbsp;</p>
                <p className="text-xs font-mono font-bold">&nbsp;</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] mb-0.5">&nbsp;</p>
                <p className="text-[10px]">&nbsp;</p>
              </div>
            </>
          )}
        </div>

        {/* Yes/No 概率进度条 */}
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0ECB81] shrink-0" />
              <span className="text-[#0ECB81] font-bold font-mono">{t('common.yes')} {market.yesPercent}%</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="text-[#F6465D] font-bold font-mono">{market.noPercent}%</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#F6465D] shrink-0" />
              <span className="text-[#F6465D] font-bold font-mono">{t('common.no')}</span>
            </span>
          </div>
          <div className="h-1 rounded-full overflow-hidden bg-white/5 flex">
            <div
              className="progress-yes rounded-l-full transition-all duration-500"
              style={{ width: `${market.yesPercent}%` }}
            />
            <div
              className="progress-no rounded-r-full transition-all duration-500"
              style={{ width: `${market.noPercent}%` }}
            />
          </div>
        </div>

        {/* 底部统计信息 */}
        <div className="flex items-center justify-between pt-2.5 border-t border-white/5">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="text-brand font-mono font-semibold">
              ${formatLargeNumber(market.totalVolume)}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3 shrink-0" />
              <span>{market.participantCount}</span>
            </span>
          </div>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            <EndTimeCountdown endTime={market.endTime} />
          </span>
        </div>
      </div>
    </Link>
  );
}
